'use client';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';
import { X, GitCompare } from 'lucide-react';
import { SCHOOLS } from '@/data/schools';
import AppImage from '@/components/ui/AppImage';
import { trackCompareSchools } from '@/lib/analytics/ga4Events';

interface CompareBarProps {
  compareList: string[];
  onRemove: (id: string) => void;
  onClear: () => void;
}

export default function CompareBar({ compareList, onRemove, onClear }: CompareBarProps) {
  if (compareList.length === 0) return null;

  const schools = compareList.map((id) => SCHOOLS.find((s) => s.id === id)).filter(Boolean);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-border shadow-lg animate-slide-up">
      <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 lg:px-8 xl:px-10 py-2.5 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-4">
          <div className="flex items-center gap-1.5 shrink-0">
            <GitCompare size={16} className="text-primary" />
            <span className="text-xs sm:text-sm font-semibold text-foreground">
              <span className="hidden sm:inline">Compare </span>({compareList.length})
            </span>
          </div>

          <div className="flex-1 flex items-center gap-2 overflow-x-auto scrollbar-thin">
            {schools.map((school) => {
              if (!school) return null;
              return (
                <div
                  key={`compare-bar-${school.id}`}
                  className="flex items-center gap-1.5 px-2 sm:px-3 py-1.5 bg-muted rounded-lg shrink-0"
                >
                  <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-md overflow-hidden bg-white border border-border">
                    <AppImage src={school.logoUrl} alt={`${school.shortName} logo`} width={28} height={28} unoptimized />
                  </div>
                  <span className="text-xs sm:text-sm font-medium text-foreground max-w-[80px] sm:max-w-[120px] truncate">{school.shortName}</span>
                  <button
                    onClick={() => onRemove(school.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors p-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <button
              onClick={onClear}
              className="text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors px-1"
            >
              Clear
            </button>
            <Link
              href={`/school-profile-comparison`}
              onClick={() => {
                const schoolNames = schools.filter(Boolean).map(s => s!.name).join(', ');
                trackCompareSchools({
                  number_of_schools: compareList.length,
                  school_names: schoolNames || undefined,
                });
              }}
              className={`px-3 sm:px-5 py-2.5 rounded-lg text-xs sm:text-sm font-semibold transition-all duration-150 active:scale-95 ${
                compareList.length >= 1
                  ? 'bg-primary text-white hover:bg-primary/90' :'bg-muted text-muted-foreground cursor-not-allowed'
              }`}
            >
              Compare
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}